import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { ChefHat, Mail, Lock, Eye, EyeOff, MailCheck, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { LaunchCounter } from "@/components/launch-counter";

const searchSchema = z.object({
  mode: z.enum(["signin", "signup"]).optional(),
  redirect: z.string().optional(),
  /** Sprint 24.1 — flag impostato quando l'utente proviene da un invio richiesta non autenticato. */
  pending: z.enum(["1"]).optional(),
});

export const Route = createFileRoute("/auth")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Accedi — ChefConnect" },
      { name: "description", content: "Accedi o registrati su ChefConnect: la piattaforma italiana per chef privati e clienti." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const { mode, redirect, pending } = useSearch({ from: "/auth" });
  // Sprint 24.1 — quando l'utente arriva con una richiesta in sospeso
  // mostriamo di default il tab "Registrati" (cliente nuovo) con microcopy.
  const defaultTab: "signin" | "signup" = mode ?? (pending ? "signup" : "signin");
  const [tab, setTab] = useState<"signin" | "signup">(defaultTab);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupSentTo, setSignupSentTo] = useState<string | null>(null);
  const [resending, setResending] = useState(false);

  // Sprint 25.2 — gli admin entrano direttamente nel pannello amministrativo.
  const resolveDestination = async (): Promise<string> => {
    if (redirect) return redirect;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: isAdmin } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
        if (isAdmin) return "/dashboard/admin";
      }
    } catch { /* fall back */ }
    return "/dashboard";
  };
  const goAfterAuth = async () => {
    const to = await resolveDestination();
    navigate({ to, replace: true } as never);
  };

  // Sprint 25.1 — Auth Redirect Fix.
  // Se l'utente arriva su /auth con una sessione già attiva (es. ritorno
  // da Google OAuth, oppure tab già loggata), reindirizza subito alla
  // destinazione intenzionale (`redirect`) o alla dashboard.
  // Evita doppi redirect tramite un ref di guardia.
  const redirectedRef = useRef(false);
  useEffect(() => {
    let mounted = true;
    const go = async () => {
      if (!mounted || redirectedRef.current) return;
      redirectedRef.current = true;
      const to = await resolveDestination();
      if (mounted) navigate({ to, replace: true } as never);
    };
    // Check sessione iniziale (gestisce ritorno da OAuth e utente già loggato).
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) void go();
    });
    // Cattura SIGNED_IN successivo (es. callback OAuth che imposta la sessione
    // dopo il mount).
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session) void go();
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onGoogle = async () => {
    try {
      const res = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin + "/auth",
      });
      if (res.error) {
        toast.error("Accesso con Google non riuscito");
        return;
      }
      if (!res.redirected) await goAfterAuth();
    } catch (e) {
      toast.error("Errore inatteso");
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "signup") {
        const { data: signupData, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin + "/dashboard",
            data: { full_name: fullName },
          },
        });
        if (error) throw error;
        // Se non c'è sessione attiva, serve conferma email — non navigare al dashboard (rimbalzerebbe a /auth).
        if (signupData.session) {
          toast.success("Account creato!");
          await goAfterAuth();
        } else {
          setSignupSentTo(email);
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Bentornato!");
        await goAfterAuth();
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Errore");
    } finally {
      setLoading(false);
    }
  };

  const onForgot = async () => {
    if (!email) return toast.error("Inserisci la tua email");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/reset-password",
    });
    if (error) return toast.error(error.message);
    toast.success("Ti abbiamo inviato il link per reimpostare la password.");
  };

  const onResend = async () => {
    if (!signupSentTo) return;
    setResending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: signupSentTo,
        options: { emailRedirectTo: window.location.origin + "/dashboard" },
      });
      if (error) throw error;
      toast.success("Email di conferma reinviata.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Errore");
    } finally {
      setResending(false);
    }
  };

  const openMailApp = () => {
    const domain = signupSentTo?.split("@")[1]?.toLowerCase() ?? "";
    const url =
      domain.includes("gmail") ? "https://mail.google.com" :
      domain.includes("outlook") || domain.includes("hotmail") || domain.includes("live") ? "https://outlook.live.com/mail/" :
      domain.includes("yahoo") ? "https://mail.yahoo.com" :
      domain.includes("icloud") || domain.includes("me.com") || domain.includes("mac.com") ? "https://www.icloud.com/mail" :
      `https://${domain || "mail.google.com"}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="container mx-auto grid min-h-[calc(100vh-4rem)] max-w-6xl gap-10 px-4 py-10 sm:py-16 md:grid-cols-2">
      <div className="hidden flex-col justify-center md:flex">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-gold">
            <ChefHat className="h-4 w-4" />
          </span>
          <span className="font-display text-xl">Chef<span className="text-gold">&nbsp;Connect</span></span>
        </Link>
        <h1 className="mt-10 font-display text-5xl leading-tight">
          Benvenuto nella<br /><span className="text-gold italic">community</span> dei chef.
        </h1>
        <p className="mt-4 max-w-md text-muted-foreground">
          Trova chef privati, gestisci richieste, ricevi recensioni. Tutto in un'unica piattaforma.
        </p>
        <div className="mt-8">
          <LaunchCounter variant="block" />
        </div>
      </div>

      <div className="flex flex-col justify-center">
        <h1 className="mb-6 text-center font-display text-3xl md:hidden">
          Accedi a <span className="text-gold">ChefConnect</span>
        </h1>
        {pending && !signupSentTo && (
          <div className="mx-auto mb-4 w-full max-w-sm rounded-xl border border-gold/40 bg-gold-muted/20 p-4 text-sm">
            <p className="font-medium">Crea gratuitamente il tuo account</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Per inviare la richiesta allo chef è necessario creare un account gratuito.
              Tempo richiesto: meno di 30 secondi. La tua richiesta verrà inviata automaticamente.
            </p>
          </div>
        )}
        <div className="mx-auto w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl">
          {signupSentTo ? (
            <div className="text-center">
              <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-gold-muted/40">
                <MailCheck className="h-7 w-7 text-gold" />
              </div>
              <h2 className="mt-4 font-display text-2xl">Controlla la tua email</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Ti abbiamo inviato un'email di conferma a <span className="font-medium text-foreground">{signupSentTo}</span>.
                Clicca il link per attivare l'account.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">Controlla anche la cartella Spam.</p>
              <div className="mt-5 space-y-2">
                <Button onClick={openMailApp} className="h-11 w-full bg-foreground text-background hover:bg-foreground/90">
                  Apri la tua email
                </Button>
                <Button onClick={onResend} disabled={resending} variant="outline" className="h-11 w-full">
                  {resending ? "Invio…" : "Reinvia email"}
                </Button>
                <Button
                  onClick={() => { setSignupSentTo(null); setPassword(""); }}
                  variant="ghost"
                  className="h-11 w-full"
                >
                  Cambia indirizzo
                </Button>
              </div>
            </div>
          ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as "signin" | "signup")}>
            <TabsList className="grid h-11 w-full grid-cols-2">
              <TabsTrigger value="signin" className="h-9">Accedi</TabsTrigger>
              <TabsTrigger value="signup" className="h-9">Registrati</TabsTrigger>
            </TabsList>

            <Button onClick={onGoogle} variant="outline" className="mt-5 h-11 w-full" type="button">
              <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.9l5.7-5.7C33.4 6.7 28.9 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19c10.5 0 19-7.9 19-19 0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.9l5.7-5.7C33.4 6.7 28.9 5 24 5 16.3 5 9.7 9.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 43c4.8 0 9.2-1.6 12.6-4.4l-5.8-4.9c-2 1.4-4.5 2.3-6.8 2.3-5.3 0-9.7-2.6-11.3-7L6.2 33.6C9.6 38.8 16.3 43 24 43z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.3-4.4 5.7l5.8 4.9c4-3.7 6.3-9.1 6.3-15.1 0-1.2-.1-2.3-.4-3.5z"/></svg>
              Continua con Google
            </Button>

            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" /> oppure <div className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={onSubmit} className="space-y-3">
              {tab === "signup" && (
                <div className="space-y-1.5">
                  <Label htmlFor="name">Nome completo</Label>
                  <Input id="name" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} required className="h-11" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="email" type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-9" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete={tab === "signup" ? "new-password" : "current-password"}
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 pl-9 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                    className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="h-11 w-full bg-foreground text-background hover:bg-foreground/90">
                {loading ? "Attendi..." : tab === "signup" ? "Crea account" : "Accedi"}
              </Button>
              {tab === "signup" && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <Check className="h-3.5 w-3.5 text-emerald-500" />
                  <span>Nessuna Partita IVA richiesta per iniziare.</span>
                </div>
              )}
            </form>

            <TabsContent value="signin" className="mt-3">
              <button type="button" onClick={onForgot} className="text-xs text-muted-foreground hover:text-foreground">
                Hai dimenticato la password?
              </button>
            </TabsContent>
            <TabsContent value="signup" className="mt-3 text-xs text-muted-foreground">
              {pending ? (
                <>La registrazione è gratuita e serve esclusivamente per permettere allo chef di risponderti. Nessun pagamento è richiesto.</>
              ) : (
                <>Registrandoti accetti i nostri Termini e l'Informativa Privacy.</>
              )}
            </TabsContent>
          </Tabs>
          )}
        </div>
      </div>
    </div>
  );
}